import { useRef, useState, lazy, Suspense } from 'react';
import { X, Download, Copy, Check, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';

// ⚡ Performance Optimization: Lazy load QR code library (~25KB) only when modal opens
// This removes the library from the main bundle, improving initial page load
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);

/**
 * Logo watermark settings for QR codes.
 * Free users get the Armenian Link logo in the center.
 * Plus users can remove the logo (or add their own in the future). ✌️
 */
const LOGO_IMAGE_SETTINGS = {
  src: '/assets/logo.png',
  height: 48,
  width: 48,
  excavate: true, // Clear space behind the logo
};

/** Preset color themes for Plus users */
const COLOR_PRESETS = [
  { name: 'Classic', fg: '#000000', bg: '#ffffff' },
  { name: 'Ocean', fg: '#0369a1', bg: '#f0f9ff' },
  { name: 'Forest', fg: '#166534', bg: '#f0fdf4' },
  { name: 'Sunset', fg: '#c2410c', bg: '#fff7ed' },
  { name: 'Purple', fg: '#7c3aed', bg: '#faf5ff' },
  { name: 'Night', fg: '#f8fafc', bg: '#0f172a' },
];

export function QRCodeModal() {
  const { qrModalLink, closeQrModal } = useUIStore();
  const {
    hasAccess: hasBrandedQR,
    requiredTier: brandedQRTier,
    openUpgradeModal,
    UpgradeModalElement,
  } = useFeatureGate('branded_qr_codes', 'Customize QR code colors and remove the watermark');
  const qrRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [size, setSize] = useState(256);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [includeLogo, setIncludeLogo] = useState(true);

  // Plus users can customize, free users get the Armenian Link watermark
  const showLogo = hasBrandedQR ? includeLogo : true;

  // Scale logo size proportionally to QR code size
  const logoSize = Math.round(size * 0.2); // Logo is ~20% of QR code size

  // QR code URL includes tracking parameter to differentiate QR scans from direct clicks
  const qrUrl = qrModalLink
    ? `${qrModalLink.url}${qrModalLink.url.includes('?') ? '&' : '?'}_qr=1`
    : '';

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', closeQrModal, !!qrModalLink);

  if (!qrModalLink) return null;

  const handleDownload = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `qr-${qrModalLink.shortCode}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrModalLink.url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeQrModal} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-sm mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">QR Code</h2>
          <button
            onClick={closeQrModal}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close QR code modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center space-y-4">
          {/* QR Code - render at 2x for retina displays */}
          <div
            ref={qrRef}
            className="p-4 rounded-lg shadow-sm"
            style={{ backgroundColor: bgColor }}
          >
            <Suspense
              fallback={
                <div
                  className="bg-muted animate-pulse rounded-lg"
                  style={{ width: size, height: size }}
                  aria-label="Loading QR code..."
                />
              }
            >
              <LazyQRCodeCanvas
                value={qrUrl}
                size={size * 2}
                level={showLogo ? 'H' : 'M'} // Higher error correction when logo is present
                marginSize={2}
                fgColor={fgColor}
                bgColor={bgColor}
                imageSettings={
                  showLogo
                    ? {
                        src: LOGO_IMAGE_SETTINGS.src,
                        height: logoSize * 2,
                        width: logoSize * 2,
                        excavate: LOGO_IMAGE_SETTINGS.excavate,
                      }
                    : undefined
                }
                style={{ width: size, height: size }}
              />
            </Suspense>
          </div>
          {!hasBrandedQR && (
            <p className="text-xs text-muted-foreground text-center">
              Upgrade to Plus to customize colors & remove watermark ✌️
            </p>
          )}

          {/* Short URL */}
          <div className="w-full">
            <p className="text-sm text-muted-foreground text-center mb-1">Short URL</p>
            <div className="flex items-center justify-center gap-2">
              <code className="px-3 py-1.5 bg-muted rounded text-sm font-mono">
                arml.ink/{qrModalLink.shortCode}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label={copied ? 'Link copied' : 'Copy link to clipboard'}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Size selector */}
          <div className="w-full">
            <label className="block text-sm text-muted-foreground text-center mb-2">Size</label>
            <div className="flex justify-center gap-2">
              {[128, 256, 512].map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    size === s ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {s}px
                </button>
              ))}
            </div>
          </div>

          {/* Color & Logo Options - Plus Feature */}
          <div className="w-full border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Customize
                {!hasBrandedQR && <TierBadge tier={brandedQRTier} size="sm" />}
              </span>
              {hasBrandedQR && (
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="text-sm text-primary hover:underline"
                >
                  {showColorPicker ? 'Hide' : 'Show'} options
                </button>
              )}
            </div>

            {hasBrandedQR && showColorPicker && (
              <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                {/* Logo toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLogo}
                    onChange={(e) => setIncludeLogo(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">Include Armenian Link logo</span>
                </label>

                {/* Color presets */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Color presets</p>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          setFgColor(preset.fg);
                          setBgColor(preset.bg);
                        }}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          fgColor === preset.fg && bgColor === preset.bg
                            ? 'border-primary ring-1 ring-primary'
                            : 'hover:border-primary/50'
                        }`}
                        style={{
                          backgroundColor: preset.bg,
                          color: preset.fg,
                        }}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom colors */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">Foreground</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={fgColor}
                        onChange={(e) => setFgColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border"
                      />
                      <input
                        type="text"
                        value={fgColor}
                        onChange={(e) => setFgColor(e.target.value)}
                        className="flex-1 px-2 py-1 text-xs rounded border bg-background uppercase"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">Background</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border"
                      />
                      <input
                        type="text"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="flex-1 px-2 py-1 text-xs rounded border bg-background uppercase"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!hasBrandedQR && (
              <button
                onClick={openUpgradeModal}
                className="block w-full px-4 py-2 rounded-md border border-dashed bg-muted/30 text-center text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                Upgrade to customize colors & remove logo
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={closeQrModal}
            className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>
      </div>

      {/* Upgrade Modal */}
      {UpgradeModalElement}
    </div>
  );
}
